import { Component, OnInit } from '@angular/core';
import { importedModules } from './modules';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthService } from 'app/auth/services/auth.service';
import { MasterUserService } from 'app/my-account/services/master-user.service';
import { confirmPasswordValidator } from 'app/utils/formValidators';
import {
  PASSWORD_CONFIRM_CUSTOM_MESSAGES,
  PASSWORD_PROFILE_MAX_LENGTH,
  PASSWORD_PROFILE_MIN_LENGTH,
  PASSWORD_PROFILE_PATTERN_CUSTOM_MESSAGES,
  PASSWORD_REGEX_MAX_LENGTH_20,
} from 'app/shared/password-validation';
import { AlertController } from 'app/shared/services/alert-controller.service';
import { User } from 'app/shared/interfaces/user';

@Component({
  selector: 'app-my-account',
  imports: importedModules,
  templateUrl: './my-account.component.html',
  styleUrl: './my-account.component.scss',
})
export class MyAccountComponent implements OnInit {
  readonly passwordProfilePatternCustomMessages = PASSWORD_PROFILE_PATTERN_CUSTOM_MESSAGES;
  readonly passwordConfirmCustomMessages = PASSWORD_CONFIRM_CUSTOM_MESSAGES;

  myAccountForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: DynamicDialogRef,
    private authService: AuthService,
    private masterUserService: MasterUserService,
    private alert: AlertController
  ) {
    this.myAccountForm = this.fb.nonNullable.group({
      email_address: [null, [Validators.required, Validators.email]],
      first_name: [null, Validators.required],
      last_name: [null, Validators.required],
      password: [null, [
        Validators.minLength(PASSWORD_PROFILE_MIN_LENGTH),
        Validators.maxLength(PASSWORD_PROFILE_MAX_LENGTH),
        Validators.pattern(PASSWORD_REGEX_MAX_LENGTH_20),
      ]],
      confirm_password: [null, [confirmPasswordValidator()]],
    });
  }

  ngOnInit(): void {
    if (this.authService.userId) {
      this.getUser();
      return;
    }

    this.onClose();
    this.alert.showToast({
      message: 'User not found',
      severity: 'error'
    });
  }

  /** Get the user information */
  getUser() {
    this.masterUserService.getById(this.authService.userId).subscribe({
      next: (resp: User) => {
        this.myAccountForm.patchValue(resp);
      }
    });
  }

  /**
   * Update the confirm password validator when the password is changed
   */
  onPasswordChange() {
    const _password = this.myAccountForm.get('password')?.value;
    if (_password) {
      this.myAccountForm.get('confirm_password')?.updateValueAndValidity();
    } else {
      this.myAccountForm.get('confirm_password')?.reset();
    }
  }

  /** Update the user information */
  onSubmit() {
    if (this.myAccountForm.invalid) {
      this.myAccountForm.markAllAsTouched();
      return;
    }

    this.masterUserService.update(
      this.authService.userId, this.myAccountForm.value
    ).subscribe({
      next: (resp: {id: string, detail: string}) => {
        this.onClose();
        this.alert.showToast({
          message: resp.detail,
          severity: 'success'
        });
      }
    });
  }

  /** Close the dialog */
  onClose() {
    this.dialogRef.close();
  }
}
